import { useState } from 'preact/hooks';
import { useLocation } from 'preact-iso';
import { api, ApiError } from '../../lib/api.js';
import { useApi } from '../../hooks/useApi.js';
import { Button } from '../../components/Button.jsx';
import { TERRACOTTA, JOB_STATUS_LABEL, JOB_STATUS_PILL, formatDueDate } from './jobs-shared.js';

export const JobsIndexPage = () => {
  const { route } = useLocation();
  const [status, setStatus] = useState('');
  const [assigneeUserId, setAssigneeUserId] = useState('');
  const [quickTitle, setQuickTitle] = useState('');
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState(null);

  const { data: users } = useApi('/jobs/assignable-users');

  const params = new URLSearchParams();
  if (status) params.set('status', status);
  if (assigneeUserId) params.set('assigneeUserId', assigneeUserId);
  const qs = params.toString();
  const { data: jobsList } = useApi(`/jobs${qs ? `?${qs}` : ''}`);

  const quickAdd = async () => {
    const title = quickTitle.trim();
    if (!title) return;
    setCreating(true);
    setError(null);
    try {
      const job = await api.post('/jobs', { title });
      setQuickTitle('');
      route(`/jobs/${job.id}`);
    } catch (err) {
      setError(err);
    } finally {
      setCreating(false);
    }
  };

  return (
    <div>
      <section class="mb-5 flex items-start justify-between gap-4">
        <div>
          <div class="mb-1.5 flex items-center gap-2">
            <span class="block h-2.5 w-2.5 rounded-full" style={{ background: TERRACOTTA }} />
            <span class="text-[11px] font-semibold uppercase tracking-eyebrow" style={{ color: TERRACOTTA }}>Jobs</span>
          </div>
          <h1 class="m-0 mb-1.5 text-[30px] leading-[1.18] tracking-tight text-text-primary">Jobs</h1>
          <p class="m-0 text-sm text-text-secondary">Job tickets — number, details, due date, assignee.</p>
        </div>
      </section>

      <div class="mb-4 flex flex-wrap items-center gap-2">
        <input
          type="text"
          value={quickTitle}
          onInput={(e) => setQuickTitle(e.currentTarget.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') quickAdd(); }}
          placeholder="Quick-add a job title…"
          class="w-64 max-w-full rounded-md border border-border-strong bg-bg-card px-3 py-1.5 text-sm placeholder:text-text-muted focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
        />
        <Button variant="primary" onClick={quickAdd} disabled={creating || !quickTitle.trim()}>
          {creating ? 'Adding…' : '+ Add job'}
        </Button>

        <select
          value={status}
          onChange={(e) => setStatus(e.currentTarget.value)}
          class="rounded-md border border-border-strong bg-bg-card px-2.5 py-1.5 text-sm text-text-primary focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
        >
          <option value="">All statuses</option>
          {Object.entries(JOB_STATUS_LABEL).map(([v, label]) => <option key={v} value={v}>{label}</option>)}
        </select>

        <select
          value={assigneeUserId}
          onChange={(e) => setAssigneeUserId(e.currentTarget.value)}
          class="rounded-md border border-border-strong bg-bg-card px-2.5 py-1.5 text-sm text-text-primary focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
        >
          <option value="">Anyone</option>
          {(users ?? []).map((u) => <option key={u.id} value={u.id}>{u.displayName}</option>)}
        </select>

        <span class="ml-auto text-[12px] text-text-tertiary">
          {jobsList ? `${jobsList.length} job${jobsList.length === 1 ? '' : 's'}` : ''}
        </span>
      </div>

      {error && (
        <p class="mb-3 text-[13px] font-semibold text-ink-magenta">
          {error instanceof ApiError ? error.message : (error.message ?? 'Failed to create job')}
        </p>
      )}

      {!jobsList && <p class="text-sm text-text-tertiary">Loading…</p>}
      {jobsList && jobsList.length === 0 && (
        <div class="surface-card p-6 text-center">
          <p class="m-0 font-serif text-lg font-medium text-text-primary">No jobs.</p>
        </div>
      )}
      {jobsList && jobsList.length > 0 && (
        <div class="space-y-1.5">
          {jobsList.map((j) => (
            <button
              key={j.id}
              type="button"
              onClick={() => route(`/jobs/${j.id}`)}
              class="relative flex w-full flex-wrap items-center gap-3 overflow-hidden rounded-md border border-border-subtle bg-bg-card p-3 text-left transition hover:border-border-strong hover:bg-bg-hover"
            >
              <span class="pointer-events-none absolute inset-y-0 left-0 w-[3px]" style={{ background: TERRACOTTA }} aria-hidden="true" />
              <span class="w-20 shrink-0 font-mono text-[12px] text-text-tertiary">{j.jobNumber}</span>
              <span class="min-w-0 flex-1 truncate font-serif text-[15px] font-medium text-text-primary">{j.title}</span>
              {j.linkedIssueCount > 0 && (
                <span class="text-[11px] text-text-tertiary">{j.linkedIssueCount} issue{j.linkedIssueCount === 1 ? '' : 's'}</span>
              )}
              <span class="text-[12px] text-text-tertiary">{j.assigneeName ?? 'Unassigned'}</span>
              <span class="text-[12px] text-text-tertiary">{formatDueDate(j.dueDate)}</span>
              <span class={`rounded-full border px-2 py-[1px] text-[10px] font-semibold uppercase tracking-[0.04em] ${JOB_STATUS_PILL[j.status]}`}>
                {JOB_STATUS_LABEL[j.status]}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
